import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  MenuItem,
  TextField,
  List,
  ListItemButton,
  Divider,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material';
import api from '../../api/axios';
import HrEmployeeProfile from './HrEmployeeProfile';

/* ─────────────────────────────────────────────
   STATUS COLOR HELPER
───────────────────────────────────────────── */
const statusColor = (s) => {
  if (s === 'APPROVED')  return 'success';
  if (s === 'REJECTED')  return 'error';
  if (s === 'VERIFIED')  return 'info';
  return 'warning';
};

/* ─────────────────────────────────────────────
   ONBOARDING APPROVAL CENTER
───────────────────────────────────────────── */
const HROnboardingApprovalCenter = () => {
  const [branches,           setBranches]           = useState([]);
  const [branchId,           setBranchId]           = useState('');
  const [status,             setStatus]             = useState('SUBMITTED');
  const [records,            setRecords]            = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [listLoading,        setListLoading]        = useState(false);

  /* ── LOAD BRANCHES ── */
  useEffect(() => {
    api.get('/branches')
      .then(res => setBranches(res.data))
      .catch(() => {});
  }, []);

  /* ── LOAD ONBOARDING LIST ── */
  const loadRecords = () => {
    if (!branchId) return;
    setListLoading(true);
    setSelectedEmployeeId(null);
    api.get(`/onboarding/branch/${branchId}/status/${status}`)
      .then(res => setRecords(res.data || []))
      .catch(() => setRecords([]))
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    loadRecords();
  }, [branchId, status]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Onboarding Approval Center
      </Typography>

      <Grid container spacing={3}>

        {/* ── LEFT PANEL: Filters + List ── */}
        <Grid item xs={12} md={selectedEmployeeId ? 4 : 12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>

            {/* Branch selector */}
            <TextField
              select
              fullWidth
              label="Select Branch"
              value={branchId}
              onChange={e => setBranchId(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            >
              {branches.map(branch => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Status filter — includes VERIFIED */}
            <TextField
              select
              fullWidth
              label="Status"
              value={status}
              onChange={e => setStatus(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            >
              {['SUBMITTED', 'VERIFIED', 'APPROVED', 'REJECTED'].map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            {/* Loading indicator */}
            {listLoading && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Record list */}
            {!listLoading && (
              <List dense sx={{ mt: 1 }}>
                {records.map(rec => (
                  <ListItemButton
                    key={rec.employeeId}
                    selected={rec.employeeId === selectedEmployeeId}
                    onClick={() => setSelectedEmployeeId(rec.employeeId)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.50',
                        borderLeft: '3px solid',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>
                        {rec.employeeName || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {rec.employeeCode}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={rec.status}
                      color={statusColor(rec.status)}
                      sx={{ ml: 1, fontWeight: 600, flexShrink: 0 }}
                    />
                  </ListItemButton>
                ))}

                {records.length === 0 && !listLoading && (
                  <Typography
                    sx={{ mt: 3, mb: 1 }}
                    color="text.secondary"
                    align="center"
                    variant="body2"
                  >
                    {branchId
                      ? `No ${status.toLowerCase()} records found`
                      : 'Select a branch to view records'}
                  </Typography>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* ── RIGHT PANEL: Profile ── */}
        {selectedEmployeeId && (
          <Grid item xs={12} md={8}>
            {/*
              Pass loadRecords as a callback so HrEmployeeProfile can
              trigger a list refresh after approve/reject/verify actions.
            */}
            <HrEmployeeProfile
              employeeId={selectedEmployeeId}
              onStatusChange={loadRecords}
            />
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default HROnboardingApprovalCenter;